import { IconButton, TableCell, TableRow } from "@mui/material";
import { useState, useEffect } from "react";
import VisitDataModal from "../Modal/VisitDataModal";
import ArrowCircleDownOutlinedIcon from "@mui/icons-material/ArrowCircleDownOutlined";
import ArrowCircleRightOutlinedIcon from "@mui/icons-material/ArrowCircleRightOutlined";
import CalendarGridPOBData from "../calender-grid/CalendarGridPOBData";
import { DownloadJSON } from "../../../../../domain/models/daily-visit-report/downloadJSON";
import { MonthlyDataType } from "../../../../../domain/models/daily-pob-report/dailypobreport";

type HqType = {
  user_id: number;
  hq_id: number;
  hq_name: string;
  tso_name: string;
  tso_status: string;
  parent_headquarter_id: number;
  daily_pob_target: number;
  total_Pob: number;
  hq_target: number;
  monthly_data: MonthlyDataType[];
};

type AreaType = {
  area_id: number;
  area_name: string;
  abm_name: string;
  headquarters: HqType[];
};

type Props = {
  data: AreaType;
  month: number;
  year: number;
  type: string;
  departmentId: string | null;
  downloadJSON: DownloadJSON[];
  customerTypeId: string | null;
};

const cssArrayStyle = {
  border: "2px solid white",
  wordWrap: "break-word",
  overflowWrap: "break-word",
  padding: 0,
  fontSize: 12,
  width: 80,
  textAlign: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const AreaWisePOB: React.FC<Props> = ({
  data,
  month,
  year,
  type,
  departmentId,
  downloadJSON,
  customerTypeId,
}) => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const [totalRow, setTotalRow] = useState<JSX.Element[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [totalArea, setTotalArea] = useState<number[]>(
    new Array(daysInMonth).fill(0)
  );
  const [open, setOpen] = useState<boolean>(false);
  const [openRow, setOpenRow] = useState<boolean>(true);
  const [day, setDay] = useState<number>(0);
  const [userId, setUserId] = useState<number>(0);
  const [name, setName] = useState<string>("");
  const [dailyPOBTarget, setDailyPOBTarget] = useState<number>(0);
  const [percentagePOBTarget, setPercentagePOBTarget] = useState<number>(0);

  const openModal = (day: number, user_id: number, name: string) => {
    setOpen((prevOpen) => !prevOpen);
    setUserId(user_id);
    setName(name);
    setDay(day);
  };

  useEffect(() => {
    const arr = new Array(daysInMonth).fill(0);
    data.headquarters.forEach((hq, key) => {
      hq.monthly_data.forEach((month, key) => {
        if (type === "rupee" && month.rupees !== undefined) {
          arr[month.day - 1] += month.rupees;
        } else if (type === "order_count" && month.order_count !== undefined) {
          arr[month.day - 1] += month.order_count;
        } else if (type === "quantity" && month.quantity !== undefined) {
          arr[month.day - 1] += month.quantity;
        }
      });
    });
    calculateTotalDailyPOBTarget(data);
    handleTotalArea(arr);
    setTotalArea(arr);
  }, [type, month, year]);

  const areaJSON: any = {};
  areaJSON.role = "ABM";
  areaJSON.user = data.abm_name;
  areaJSON.territory = data.area_name;

  const handleTotalArea = (area: number[]) => {
    let totalSum = 0;
    for (const item of area) {
      if (typeof item === "number") {
        totalSum += item;
      } else if (item === "H" || item === 0) {
        totalSum += 0;
      }
    }
    setTotal(totalSum);
    areaJSON.total = type === "rupee" ? (totalSum / 100000).toFixed(1) : totalSum;
    const getDayOfWeek = (day: number) => {
      return new Date(year, month - 1, day).getDay();
    };
    const totalRowCells = area.map((total, index) => {
      const isSunday = getDayOfWeek(index + 1) === 0;
      areaJSON[index + 1] = isSunday
        ? "H"
        : type === "rupee"
        ? (total / 1000).toFixed(1)
        : total;
      return (
        <TableCell
          key={index}
          sx={{
            border: "2px solid white",
            padding: 0,
            textAlign: "center",
            fontSize: 12,
            backgroundColor: "#b6dffa",
          }}
        >
          {isSunday
            ? "H"
            : type === "rupee"
            ? (total / 1000).toFixed(1)
            : total}
        </TableCell>
      );
    });
    setTotalRow(totalRowCells);

    const existingIndex = downloadJSON.findIndex(
      (item) => item.user === areaJSON.user
    );

    if (existingIndex !== -1) {
      downloadJSON[existingIndex] = areaJSON;
    } else {
      downloadJSON.push(areaJSON);
    }
  };

  const calculateTotalDailyPOBTarget = (data: any) => {
    let totalPOBTarget = data.headquarters.reduce(
      (total: any, hq: any) => total + hq.daily_pob_target,
      0
    );
    let hqTotalPOB = data.headquarters.reduce(
      (total: any, hq: any) => total + hq.total_Pob,
      0
    );

    let hqTarget = data.headquarters.reduce(
      (total: any, hq: any) => total + hq.hq_target,
      0
    );

    setDailyPOBTarget(totalPOBTarget);

    if (hqTarget > 0) {
      let percentagePOB = (hqTotalPOB / hqTarget) * 100;

      setPercentagePOBTarget(percentagePOB);
      areaJSON.percentagePOBTarget =
        type === "rupee" ? percentagePOB.toFixed(1) : percentagePOB;
    } else if (hqTarget === 0 && hqTotalPOB === 0) {
      setPercentagePOBTarget(0);
      areaJSON.percentagePOBTarget = "-";
    }

    areaJSON.dailyPOBTarget =
      type === "rupee" ? (totalPOBTarget / 1000).toFixed(1) : totalPOBTarget;
  };

  return (
    <>
      <TableRow>
        <TableCell
          sx={{
            border: "2px solid white",
            padding: 0,
          }}
        >
          <IconButton size="small" onClick={() => setOpenRow(!openRow)}>
            {openRow ? (
              <ArrowCircleDownOutlinedIcon fontSize="small" />
            ) : (
              <ArrowCircleRightOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell sx={cssArrayStyle}>{data.abm_name}</TableCell>
        <TableCell
          sx={{
            border: "2px solid white",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            padding: 0,
            fontSize: 12,
            width: 60,
            textAlign: "center",
          }}
        >
          {data.area_name}
        </TableCell>
        <TableCell sx={cssArrayStyle}>
          {type === "rupee"
            ? (dailyPOBTarget / 1000).toFixed(1)
            : (dailyPOBTarget / 1000).toFixed(1)}
        </TableCell>
        <TableCell sx={cssArrayStyle}>
          {percentagePOBTarget !== null
            ? type === "rupee"
              ? `${percentagePOBTarget.toFixed(1)} %`
              : `${percentagePOBTarget.toFixed(1)} %`
            : "-"}
        </TableCell>
        <TableCell
          align="center"
          style={{
            border: "2px solid white",
            padding: 0,
            cursor: "default",
            fontSize: 12,
          }}
          colSpan={1}
        />
        <TableCell
          align="center"
          sx={{
            border: "2px solid white",
            padding: 0,
            fontSize: 12,
          }}
        >
          {type === "rupee" ? (total / 100000).toFixed(1) : total}
        </TableCell>
        {totalRow}
      </TableRow>
      {data.headquarters.map((hq) => (
        <TableRow>
          <CalendarGridPOBData
            open={openRow}
            hq={hq}
            year={year}
            month={month}
            modal={openModal}
            downloadJSON={downloadJSON}
            type={type}
            customerTypeId={customerTypeId}
          />
        </TableRow>
      ))}
      {open && (
        <VisitDataModal
          open={open}
          setOpen={setOpen}
          day={day}
          userId={userId}
          year={year}
          name={name}
          departmentId={departmentId}
          month={month}
          customerTypeId={customerTypeId}
        />
      )}
    </>
  );
};

export default AreaWisePOB;
